import { useCallback, useMemo, useState } from 'react';
import {
  FilterDropdownItem,
  FilterToolbarItem,
  FilterToolbarItemId,
  mapFiltersToFiltersState,
  SelectedFilters,
  useFilterFiltersByPermission,
} from 'src/components/Filters';
import { useDefaultYesNoFilterValues } from 'src/components/Filters/hooks/useDefaultYesNoFilterValues';
import { useFiltersHelpers } from 'src/components/FilterToolbar/useFiltersHelpers';
import { useListingFilters } from 'src/components/MainFilterBar';
import { ListingQuery } from 'src/WebApiController';

export const useInventoryListingFilters = (
  initialQuery: ListingQuery,
  filterQuery: ListingQuery,
  tempQuery: ListingQuery,
  setTempQuery: (_: ListingQuery) => void,
  resetTempQuery?: (queryKeys?: (keyof ListingQuery)[]) => void,
  isEventPage = false
) => {
  const { filters } = useListingFilters({
    isEventPage,
  });
  const filtersMap = useMemo<
    Partial<Record<FilterToolbarItemId, FilterToolbarItem>>
  >(() => {
    const filtersMap: Partial<Record<FilterToolbarItemId, FilterToolbarItem>> =
      {};
    filters.forEach((filterGroup) => {
      filterGroup.items.forEach((filterItem) => {
        filtersMap[filterItem.filterId] = filterItem;
      });
    });
    return filtersMap;
  }, [filters]);
  const filteredByPermissionsFilters = useFilterFiltersByPermission({
    filters,
  });

  const { appliedFilters } = useFiltersHelpers({
    filters,
    activeQuery: filterQuery,
    mandatoryFiltersToShow: [],
    initialQuery,
  });
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>(() =>
    mapFiltersToFiltersState({
      filters: filteredByPermissionsFilters,
      appliedFilterIds: appliedFilters,
      mandatoryFiltersToShow: [],
    })
  );

  const { yesNoFilterDefaults } = useDefaultYesNoFilterValues({});

  const onSaveSelectedFilters = useCallback(
    (updatedSelectedFilters: SelectedFilters) => {
      const localTemQuery = { ...tempQuery };
      Object.values(updatedSelectedFilters)
        .flatMap((groupedFilters) => groupedFilters)
        .forEach((filterDropdownItem) => {
          if (yesNoFilterDefaults[filterDropdownItem.filterId] !== undefined) {
            Object.assign(localTemQuery, {
              [filterDropdownItem.filterId]:
                yesNoFilterDefaults[filterDropdownItem.filterId],
            });
          }
        });
      setTempQuery?.(localTemQuery);
      setSelectedFilters(updatedSelectedFilters);
    },
    [setTempQuery, tempQuery, yesNoFilterDefaults]
  );

  const onFilterRemoved = useCallback(
    (removedFilterDropdownItem: FilterDropdownItem) => {
      const titleContentId = removedFilterDropdownItem.titleContentId as string;

      const filterToolbarGroup = filters.find(
        (group) => group.titleContentId === titleContentId
      );

      if (!filterToolbarGroup) {
        return;
      }

      const filtersToRemove = filterToolbarGroup.items.filter(
        (filter) => filter.filterId === removedFilterDropdownItem.filterId
      );

      const filterIdsToRemove = filtersToRemove.map((f) => f.filterId);
      const filteredDropdownItems = (
        selectedFilters[titleContentId] ?? []
      ).filter(
        (dropdownItem) => !filterIdsToRemove.includes(dropdownItem.filterId)
      );

      const updatedSelectedFilters = { ...selectedFilters };
      if (filteredDropdownItems.length > 0) {
        updatedSelectedFilters[titleContentId] = filteredDropdownItems;
      } else {
        delete updatedSelectedFilters[titleContentId];
      }

      // all keys to remove from tempQuery
      const filterQueryKeysToRemove = filtersToRemove.flatMap(
        (f) => f.filterQueryKeys ?? []
      );

      resetTempQuery?.(filterQueryKeysToRemove as any);
      setSelectedFilters(updatedSelectedFilters);
    },
    [filters, resetTempQuery, selectedFilters, setSelectedFilters]
  );

  return {
    filters,
    filtersMap,
    selectedFilters,
    onFilterRemoved,
    onSaveSelectedFilters,
  };
};
